/**
 * workflowSlice — cross-route ephemeral workflow state.
 *
 * Holds multi-step wizard progress, transient filter selections, and other
 * cross-route state that does not belong in the URL and is not a server entity.
 *
 * Design principles:
 * - State here is keyed by a stable workflowId string (e.g. 'onboarding', 'invite-member').
 * - Each workflow entry is a typed WorkflowEntry with a step index and opaque metadata.
 * - Metadata must be JSON-serializable and must NOT contain PII, credentials, or server entities.
 * - Workflows are cleared on completion or explicitly via clearWorkflow / clearAllWorkflows.
 * - A workflow that is not present is treated as "not started" (step 0, no metadata).
 *
 * Usage example:
 *   dispatch(setWorkflowStep({ workflowId: 'onboarding', step: 2 }))
 *   dispatch(setWorkflowMeta({ workflowId: 'onboarding', meta: { selectedHotelId: uuid } }))
 */

import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

/**
 * Serializable metadata for a workflow. Values must be JSON primitives or arrays/objects
 * of primitives. No server entity objects, no functions, no class instances.
 */
export type WorkflowMetaValue =
  | string
  | number
  | boolean
  | null
  | WorkflowMetaValue[]
  | { [key: string]: WorkflowMetaValue };

export interface WorkflowEntry {
  /** Zero-based step index within the workflow. */
  step: number;
  /** Arbitrary serializable metadata for this workflow. Non-PII only. */
  meta: Record<string, WorkflowMetaValue>;
}

export interface WorkflowState {
  /** Map of workflowId → WorkflowEntry. */
  workflows: Record<string, WorkflowEntry>;
}

const initialState: WorkflowState = {
  workflows: {},
};

export const workflowSlice = createSlice({
  name: 'workflow',
  initialState,
  reducers: {
    /**
     * Set (or advance) the step for a workflow.
     * Creates the workflow entry if it does not exist.
     */
    setWorkflowStep(state, action: PayloadAction<{ workflowId: string; step: number }>) {
      const { workflowId, step } = action.payload;
      const existing = state.workflows[workflowId];
      if (existing !== undefined) {
        existing.step = step;
      } else {
        state.workflows[workflowId] = { step, meta: {} };
      }
    },
    /**
     * Merge metadata into a workflow entry.
     * Creates the workflow entry at step 0 if it does not exist.
     */
    setWorkflowMeta(
      state,
      action: PayloadAction<{
        workflowId: string;
        meta: Record<string, WorkflowMetaValue>;
      }>,
    ) {
      const { workflowId, meta } = action.payload;
      const existing = state.workflows[workflowId];
      if (existing !== undefined) {
        existing.meta = { ...existing.meta, ...meta };
      } else {
        state.workflows[workflowId] = { step: 0, meta };
      }
    },
    /** Remove a specific workflow entry (completed or abandoned). */
    clearWorkflow(state, action: PayloadAction<string>) {
      delete state.workflows[action.payload];
    },
    /** Remove all workflow entries. Use on logout. */
    clearAllWorkflows(state) {
      state.workflows = {};
    },
  },
});

export const { setWorkflowStep, setWorkflowMeta, clearWorkflow, clearAllWorkflows } =
  workflowSlice.actions;

// Selectors
export const selectWorkflow =
  (workflowId: string) =>
  (state: { workflow: WorkflowState }): WorkflowEntry | undefined =>
    state.workflow.workflows[workflowId];

export const selectWorkflowStep =
  (workflowId: string) =>
  (state: { workflow: WorkflowState }): number =>
    state.workflow.workflows[workflowId]?.step ?? 0;

export const selectWorkflowMeta =
  (workflowId: string) =>
  (state: { workflow: WorkflowState }): Record<string, WorkflowMetaValue> =>
    state.workflow.workflows[workflowId]?.meta ?? {};
